import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { NavigationService } from '../../../../services/navigation.service';

@Component({
  selector: 'app-sidebar-plain',
  templateUrl: './sidebar-plain.component.html',
  styleUrls: ['./sidebar-plain.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule]
})
export class SidebarPlainComponent implements OnInit, OnDestroy {
  
  private routerSubscription: Subscription;
  
  constructor(
    public navService: NavigationService,
    private router: Router
  ) { }

  ngOnInit() {
    // Initialize selected item if needed
    if (!this.navService.selectedItem && this.navService.defaultMenu.length) {
      this.navService.selectedItem = this.navService.defaultMenu[0];
    }
    
    // Set active flags based on current route
    this.setActiveFlag();
    
    // Subscribe to route changes to update active flags
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.setActiveFlag();
      });
  }
  
  ngOnDestroy() {
    // Clean up subscriptions
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  /**
   * Handles menu item selection and dropdown toggle
   * @param item - The selected menu item
   */
  selectItem(item) {
    // For dropdown items, toggle the active state
    if (item.type === 'dropDown') {
      item.active = !item.active;
      
      // If this item is being activated, deactivate all other dropdowns at the same level
      if (item.active) {
        this.navService.defaultMenu.forEach(menuItem => {
          if (menuItem !== item && menuItem.type === 'dropDown') {
            menuItem.active = false;
          }
        });
      }
    }
    
    // Always set the selected item for the navigation service
    // routerLinkActive will handle the visual active state for links
    this.navService.selectedItem = item;
  }

  /**
   * Check if an item is currently active based on router state
   * This ensures only one item is active at a time by checking if it's the selectedItem
   */
  isItemActive(item: any): boolean {
    if (!item.state || item.type !== 'link') {
      return item.active || false;
    }
    
    // Only return true if this is the selected item (which is set by setActiveFlag)
    // This ensures only one item is active at a time
    return this.navService.selectedItem === item;
  }
  
  /**
   * Toggle submenu items for nested dropdowns
   * @param item - The submenu dropdown item to toggle
   * @param event - Click event
   */
  toggleSubmenu(item, event: Event) {
    // Toggle the active state without affecting other items
    item.active = !item.active;
    
    // Stop event propagation to prevent parent toggles
    event.stopPropagation();
  }
  
  /**
   * Set active flags based on current route
   * Finds the best matching menu item and sets it as selected
   */
  setActiveFlag() {
    const currentUrl = this.router.url.split('?')[0]; // Remove query params
    const normalizedRoute = currentUrl.replace(/\/$/, '');
    
    let bestMatch: any = null;
    let bestMatchLength = 0;
    
    // Find the menu item that best matches the current route (longest prefix match)
    for (const item of this.navService.defaultMenu) {
      if (item.state && item.type === 'link') {
        const normalizedState = item.state.replace(/\/$/, '');
        
        // Check for exact match first
        if (normalizedRoute === normalizedState) {
          bestMatch = item;
          bestMatchLength = normalizedState.length;
          break; // Exact match is always best
        }
        
        // Check for prefix match (route starts with item state + '/')
        if (normalizedRoute.startsWith(normalizedState + '/')) {
          // If this is a longer match than previous, update best match
          if (normalizedState.length > bestMatchLength) {
            bestMatch = item;
            bestMatchLength = normalizedState.length;
          }
        }
      }
      
      // Check first level children
      if (item.sub) {
        for (const subItem of item.sub) {
          if (subItem.state && subItem.type === 'link') {
            const normalizedState = subItem.state.replace(/\/$/, '');
            
            if (normalizedRoute === normalizedState) {
              bestMatch = item;
              bestMatchLength = normalizedState.length;
              break;
            }
            
            if (normalizedRoute.startsWith(normalizedState + '/')) {
              if (normalizedState.length > bestMatchLength) {
                bestMatch = item;
                bestMatchLength = normalizedState.length;
              }
            }
          }
          
          // Check second level children
          if (subItem.sub) {
            for (const subChildItem of subItem.sub) {
              if (subChildItem.state && subChildItem.type === 'link') {
                const normalizedState = subChildItem.state.replace(/\/$/, '');
                
                if (normalizedRoute === normalizedState) {
                  bestMatch = item;
                  bestMatchLength = normalizedState.length;
                  break;
                }
                
                if (normalizedRoute.startsWith(normalizedState + '/')) {
                  if (normalizedState.length > bestMatchLength) {
                    bestMatch = item;
                    bestMatchLength = normalizedState.length;
                  }
                }
              }
            }
          }
        }
      }
    }
    
    // Set the best match as selected (only one item will be selected)
    if (bestMatch) {
      this.navService.selectedItem = bestMatch;
    }
  }
}
